import React, { useMemo } from 'react';
import clsx from 'clsx';
import {
    PiCheckCircleLight,
    PiWarningLight,
} from 'react-icons/pi';
import FieldShell, { fieldSurface, FOCUS_RING_ONLY } from '../shared/TailwindComponents/FieldShell';
import Toggle from '../shared/TailwindComponents/Toggle';
import Dropdown from '../shared/TailwindComponents/Dropdown';
import Input from '../shared/TailwindComponents/Input';
import SingleFileInput from '../shared/TailwindComponents/SingleFileInput';
import Badge from '../shared/TailwindComponents/Badge';

export type AccessLevel = 'view' | 'edit';
export type ShareUser = {
    id: string;
    name: string;
    email: string;
    role: string;
    initials: string;
    avatarUrl: string;
    access?: AccessLevel;
    UserName?: string;
};

type HeadingKey = 'H1' | 'H2' | 'H3' | 'H4' | 'Body';

type TypographyLevel = {
    FontType?: string;
    Size?: number | string;
    Color?: string;
};


type Props = {
    t: (s: string) => string;
    formik: any;
    fontOptions: { label: string; value: string }[];
    headingStyleOptions: { label: string; value: string }[];
    extractedConfidence?: Record<string, string> | null;
};

const isFile = (x: any) => typeof File !== 'undefined' && x instanceof File;
const levelList: HeadingKey[] = ['H1', 'H2', 'H3', 'H4', 'Body'];

const clampNum = (v: any, min: number, max: number) => {
    const n = Number(v);
    if (Number.isNaN(n)) return min;
    return Math.min(max, Math.max(min, n));
};

const WORD_POINT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72];

const BrandingPanel: React.FC<Props> = ({
    t,
    formik,
    fontOptions,
    headingStyleOptions,
    extractedConfidence,
}) => {

    // Confidence badge helper for extracted sections
    const renderConfidenceBadge = (section: string) => {
        if (!extractedConfidence) return null;
        const level = extractedConfidence[section];
        if (!level) return null;
        const colors: Record<string, 'green' | 'yellow' | 'gray'> = {
            high: 'green',
            medium: 'yellow',
            low: 'gray'
        };
        return (
            <Badge color={colors[level] || 'gray'} className="ml-2 text-xs">
                {level === 'high' ? <PiCheckCircleLight className="inline mr-1" /> : level === 'low' ? <PiWarningLight className="inline mr-1" /> : null}
                {level}
            </Badge>
        );
    };

    const globalBodySize = formik.values.Branding.Typography.DocumentFontSize ?? 12;
    const globalHeadingSize = formik.values.Branding.Typography.HeadingFontSize || 'auto';

    const levels: Record<HeadingKey, TypographyLevel> = useMemo(() => {
        const lv = formik.values.Branding.Typography.Levels || {};
        const ensure = (k: HeadingKey): TypographyLevel => lv[k] || {};
        return {
            H1: ensure('H1'),
            H2: ensure('H2'),
            H3: ensure('H3'),
            H4: ensure('H4'),
            Body: ensure('Body'),
        };
    }, [formik.values.Branding?.Typography?.Levels]);

    const setLevel = (k: HeadingKey, path: keyof TypographyLevel, value: any) => {
        formik.setFieldValue(`Branding.Typography.Levels.${k}.${path}`, value);
    };

    const fallbackHeadingSize = (k: HeadingKey) => {
        if (k === 'Body') return globalBodySize;
        if (globalHeadingSize !== 'auto' && globalHeadingSize !== '' && !Number.isNaN(Number(globalHeadingSize))) {
            return Number(globalHeadingSize);
        }
        // auto ladder
        switch (k) {
            case 'H1': return 18;
            case 'H2': return 16;
            case 'H3': return 14;
            case 'H4': return 12.5;
            default: return globalBodySize;
        }
    };

    return (
        <div className="space-y-8">
            <section className="space-y-4">
                <div>
                    <h4 className="text-base font-semibold flex items-center">{t('Typography')}{renderConfidenceBadge('typography')}</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t('Set global defaults, then override per level (H1–H4, Body) as needed.')}
                    </p>
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Dropdown
                        value={formik.values.Branding.Typography.FontType}
                        onChange={(value) => formik.setFieldValue('Branding.Typography.FontType', value)}
                        options={fontOptions}
                        label={t('Default Font')}
                    />
                    <Input
                        type="number"
                        name="DocumentFontSize"
                        value={formik.values.Branding.Typography.DocumentFontSize}
                        onChange={(e) =>
                            formik.setFieldValue('Branding.Typography.DocumentFontSize', clampNum(e.target.value, 8, 20))
                        }
                        label={t('Default Body Size (pt)')}
                        infoText={t('Between 8 and 20')}
                        required
                        className="mt-3 rounded  py-[10px] w-50 placeholder-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 bg-white"
                    />
                    <Input
                        type="text"
                        name="HeadingFontSize"
                        value={formik.values.Branding.Typography.HeadingFontSize}
                        onChange={(e) => {
                            const raw = e.target.value.trim();
                            if (raw.toLowerCase() === 'auto' || raw === '') {
                                formik.setFieldValue('Branding.Typography.HeadingFontSize', 'auto');
                            } else {
                                formik.setFieldValue('Branding.Typography.HeadingFontSize', clampNum(raw, 10, 24));
                            }
                        }}
                        label={t('Default Heading Size')}
                        helperText={t('Enter "auto" or a number')}
                        required
                        className="mt-3 rounded py-[10px] w-50 placeholder-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 bg-white"
                    />
                </div>

                <div className="mt-6 space-y-4">
                    {levelList.map((level) => {
                        const l = levels[level] || {};
                        const label = level === 'Body' ? t('Body (Normal Text)') : level;
                        const sizeValue =
                            typeof l.Size === 'number'
                                ? String(l.Size)
                                : (typeof l.Size === 'string' && l.Size.toLowerCase() === 'auto') ? 'auto' : '';

                        return (
                            <div key={level} className="py-3 border-b border-gray-100 dark:border-gray-800 last:border-b-0">
                                <h5 className="text-sm font-medium text-fg-body mb-2">{label}</h5>
                                <div className="grid grid-cols-3 gap-4">
                                    <Dropdown
                                        value={l.FontType ?? ''}
                                        onChange={(value) => setLevel(level, 'FontType', value)}
                                        options={[{ label: t('Use Default'), value: '' }, ...fontOptions]}
                                        label={t('Font')}
                                        fullWidth
                                    />

                                    <Dropdown
                                        value={sizeValue}
                                        onChange={(value) => {
                                            const raw = value;
                                            if (raw === '' || raw === 'auto') {
                                                setLevel(level, 'Size', 'auto');
                                            } else {
                                                setLevel(level, 'Size', clampNum(raw, 8, 30));
                                            }
                                        }}
                                        options={WORD_POINT_SIZES.map((pt) => ({ label: `${pt} pt`, value: `${pt}` }))}
                                        label={t('Size (pt)')}
                                        fullWidth
                                    />

                                    <FieldShell id={`${level}-color-hex`} label={t('Color')} translate={false}>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="color"
                                                value={l.Color || '#000000'}
                                                onChange={(e) => setLevel(level, 'Color', e.target.value)}
                                                className={clsx(
                                                    'w-9 h-9 flex-shrink-0 rounded-md border border-hairline cursor-pointer p-0 bg-transparent',
                                                    FOCUS_RING_ONLY
                                                )}
                                                aria-label={t('Color')}
                                                title={l.Color || t('Default')}
                                            />
                                            <input
                                                id={`${level}-color-hex`}
                                                type="text"
                                                value={l.Color || ''}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    if (v === '' || /^#([0-9a-fA-F]{0,6})$/.test(v)) {
                                                        setLevel(level, 'Color', v);
                                                    }
                                                }}
                                                placeholder={t('Default')}
                                                className={clsx(fieldSurface(), 'px-3 py-1.5')}
                                            />
                                        </div>
                                    </FieldShell>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Table of Contents Section */}
            <section className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-6">
                <div>
                    <h4 className="text-base font-semibold flex items-center">{t('Table of Contents')}</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t('Auto-generated table of contents for longer documents. Standard in legal agreements over 5 pages.')}
                    </p>
                </div>
                <Toggle
                    enabled={formik.values.Branding.TableOfContents?.Enabled ?? false}
                    onToggle={(value) => formik.setFieldValue('Branding.TableOfContents.Enabled', value)}
                    label={t('Include Table of Contents')}
                    translate={false}
                />
                {formik.values.Branding.TableOfContents?.Enabled && (
                    <div className="grid grid-cols-2 gap-4">
                        <Dropdown
                            value={String(formik.values.Branding.TableOfContents?.Depth ?? 3)}
                            onChange={(value) => formik.setFieldValue('Branding.TableOfContents.Depth', Number(value))}
                            options={[
                                { label: t('H1 only'), value: '1' },
                                { label: t('H1 + H2'), value: '2' },
                                { label: t('H1 + H2 + H3'), value: '3' },
                                { label: t('All headings'), value: '4' },
                            ]}
                            label={t('Depth')}
                        />
                        <Dropdown
                            value={formik.values.Branding.TableOfContents?.Style ?? 'dotted'}
                            onChange={(value) => formik.setFieldValue('Branding.TableOfContents.Style', value)}
                            options={[
                                { label: t('Dotted leaders'), value: 'dotted' },
                                { label: t('Dashed leaders'), value: 'dashed' },
                                { label: t('No leaders'), value: 'none' },
                            ]}
                            label={t('Style')}
                        />
                    </div>
                )}
            </section>

            <section className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-6">
                <div>
                    <h4 className="text-base font-semibold flex items-center">{t('Margins')}{renderConfidenceBadge('margins')}</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t('Standardized margins ensure consistent pagination and printer-friendly PDFs.')}
                    </p>
                </div>
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {(['Top', 'Bottom', 'Left', 'Right'] as const).map((side) => (
                        <Input
                            key={side}
                            type="number"
                            name={side}
                            value={formik.values.Branding.Margins[side]}
                            onChange={(e) => formik.setFieldValue(`Branding.Margins.${side}`, parseFloat(e.target.value))}
                            label={t(side)}
                            helperText={t('In inches')}
                            required
                        />
                    ))}
                </div>
            </section>

            <section className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-6">
                <div>
                    <h4 className="text-base font-semibold flex items-center">{t('Logos & Watermark')}{renderConfidenceBadge('logos')}</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t('Add firm branding and optional watermark for draft/confidential documents.')}
                    </p>
                </div>

                <div className="mt-4 space-y-4">
                    <Toggle
                        enabled={formik.values.Branding.Logos.UseFirmLogo}
                        onToggle={(value) => formik.setFieldValue('Branding.Logos.UseFirmLogo', value)}
                        label={t('Use Firm Logo')}
                        translate={false}
                    />

                    {formik.values.Branding.Logos.UseFirmLogo && (
                        <div className="grid grid-cols-2 gap-4">
                            <SingleFileInput
                                label={t('Firm Logo')}
                                accept="image/*"
                                file={isFile(formik.values.Branding.Logos.FirmLogo) ? formik.values.Branding.Logos.FirmLogo : null}
                                setFile={(file) => formik.setFieldValue('Branding.Logos.FirmLogo', file)}
                                existingFileUrl={
                                    typeof formik.values.Branding.Logos.FirmLogo === 'string'
                                        ? formik.values.Branding.Logos.FirmLogo
                                        : undefined
                                }
                                desc={t('Used in headers/footers and cover pages')}
                            />
                            <div className="space-y-4">
                                <Toggle
                                    enabled={formik.values.Branding.Logos.Color}
                                    onToggle={(value) => formik.setFieldValue('Branding.Logos.Color', value)}
                                    label={t('Colored Logo')}
                                    translate={false}
                                />
                                <Input
                                    type="number"
                                    name="FirmLogoSize"
                                    value={formik.values.Branding.Logos.FirmLogoSize ?? 24}
                                    onChange={(e) =>
                                        formik.setFieldValue('Branding.Logos.FirmLogoSize', clampNum(e.target.value, 12, 96))
                                    }
                                    label={t('Logo Size (pt)')}
                                    infoText={t('12–96pt')}
                                />
                            </div>
                        </div>
                    )}

                    <Toggle
                        enabled={!!formik.values.Branding.Logos.UseWatermark}
                        onToggle={(value) => formik.setFieldValue('Branding.Logos.UseWatermark', value)}
                        label={t('Use Watermark')}
                        translate={false}
                    />

                    {formik.values.Branding.Logos.UseWatermark && (
                        <div className="grid grid-cols-2 gap-4">
                            <SingleFileInput
                                label={t('Watermark Logo')}
                                accept="image/*"
                                file={
                                    isFile(formik.values.Branding.Logos.WatermarkLogo)
                                        ? formik.values.Branding.Logos.WatermarkLogo
                                        : null
                                }
                                setFile={(file) => formik.setFieldValue('Branding.Logos.WatermarkLogo', file)}
                                existingFileUrl={
                                    typeof formik.values.Branding.Logos.WatermarkLogo === 'string'
                                        ? formik.values.Branding.Logos.WatermarkLogo
                                        : undefined
                                }
                                desc={t('Optional logo applied with opacity')}
                            />
                            <div className="space-y-4">
                                <Input
                                    name="WatermarkText"
                                    value={formik.values.Branding.Logos.WatermarkText ?? ''}
                                    onChange={(e) => formik.setFieldValue('Branding.Logos.WatermarkText', e.target.value)}
                                    label={t('Watermark Text')}
                                    infoText={t('Used if no watermark logo (e.g., DRAFT, CONFIDENTIAL)')}
                                    type="text"
                                />
                                <Input
                                    type="number"
                                    name="WatermarkOpacity"
                                    value={formik.values.Branding.Logos.WatermarkOpacity ?? 25}
                                    onChange={(e) =>
                                        formik.setFieldValue('Branding.Logos.WatermarkOpacity', clampNum(e.target.value, 10, 50))
                                    }
                                    label={t('Watermark Opacity (%)')}
                                    infoText={t('10–50%. Default 25%')}
                                    required
                                />
                            </div>
                        </div>
                    )}
                </div>
            </section>

            <section className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-6">
                <div>
                    <h4 className="text-base font-semibold flex items-center">{t('Header & Footer')}{renderConfidenceBadge('header_footer')}</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t('Configure page headers and footers with up to three text positions (left, center, right) and optional logos.')}
                    </p>
                </div>

                <div className="mt-4 space-y-6">
                    {/* Header Section */}
                    <div>
                        <h5 className="text-sm font-medium text-fg-body mb-3">{t('Header')}</h5>
                        
                        {/* Per-position mode selectors */}
                        <div className="grid grid-cols-3 gap-3 mb-4">
                            {(['Left', 'Center', 'Right'] as const).map((position) => {
                                const modeKey = `Header${position}Mode` as const;
                                const textKey = `HeaderText${position}` as const;
                                const imageKey = `Header${position}Image` as const;
                                const mode = formik.values.Branding.HeaderFooter[modeKey] ?? 'none';
                                
                                return (
                                    <div key={position} className="space-y-2">
                                        <Dropdown
                                            value={mode}
                                            onChange={(value) => formik.setFieldValue(`Branding.HeaderFooter.${modeKey}`, value)}
                                            options={[
                                                { label: t('None'), value: 'none' },
                                                { label: t('Text'), value: 'text' },
                                                { label: t('Logo/Image'), value: 'logo' },
                                            ]}
                                            label={t(position)}
                                        />
                                        {mode === 'text' && (
                                            <Input
                                                name={textKey}
                                                value={formik.values.Branding.HeaderFooter[textKey] ?? ''}
                                                onChange={(e) => formik.setFieldValue(`Branding.HeaderFooter.${textKey}`, e.target.value)}
                                                placeholder={position === 'Left' ? t('e.g., Firm Name') : position === 'Center' ? t('e.g., Document Title') : t('e.g., Date')}
                                                type="text"
                                            />
                                        )}
                                        {mode === 'logo' && (
                                            <SingleFileInput
                                                accept="image/*"
                                                file={isFile(formik.values.Branding.HeaderFooter[imageKey]) ? formik.values.Branding.HeaderFooter[imageKey] as File : null}
                                                setFile={(file) => formik.setFieldValue(`Branding.HeaderFooter.${imageKey}`, file)}
                                                existingFileUrl={
                                                    typeof formik.values.Branding.HeaderFooter[imageKey] === 'string' && formik.values.Branding.HeaderFooter[imageKey]
                                                        ? formik.values.Branding.HeaderFooter[imageKey] as string
                                                        : undefined
                                                }
                                                desc={t('Upload image')}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Header font settings - show if any position has text */}
                        {(formik.values.Branding.HeaderFooter.HeaderLeftMode === 'text' ||
                            formik.values.Branding.HeaderFooter.HeaderCenterMode === 'text' ||
                            formik.values.Branding.HeaderFooter.HeaderRightMode === 'text') && (
                            <div className="grid grid-cols-2 gap-4">
                                <Dropdown
                                    value={formik.values.Branding.HeaderFooter.HeaderFontType ?? ''}
                                    onChange={(value) => formik.setFieldValue('Branding.HeaderFooter.HeaderFontType', value)}
                                    options={[{ label: t('Use Default'), value: '' }, ...fontOptions]}
                                    label={t('Header Font')}
                                />
                                <Input
                                    type="number"
                                    name="HeaderFontSize"
                                    value={formik.values.Branding.HeaderFooter.HeaderFontSize ?? 10}
                                    onChange={(e) =>
                                        formik.setFieldValue('Branding.HeaderFooter.HeaderFontSize', clampNum(e.target.value, 8, 20))
                                    }
                                    label={t('Font Size (pt)')}
                                />
                            </div>
                        )}

                        {/* Header logo size - show if any position has logo */}
                        {(formik.values.Branding.HeaderFooter.HeaderLeftMode === 'logo' ||
                            formik.values.Branding.HeaderFooter.HeaderCenterMode === 'logo' ||
                            formik.values.Branding.HeaderFooter.HeaderRightMode === 'logo') && (
                            <div className="mt-3">
                                <Input
                                    type="number"
                                    name="HeaderLogoSize"
                                    value={formik.values.Branding.HeaderFooter.HeaderLogoSize ?? 20}
                                    onChange={(e) =>
                                        formik.setFieldValue('Branding.HeaderFooter.HeaderLogoSize', clampNum(e.target.value, 12, 96))
                                    }
                                    label={t('Logo Size (pt)')}
                                />
                            </div>
                        )}
                    </div>

                    {/* Footer Section */}
                    <div>
                        <h5 className="text-sm font-medium text-fg-body mb-3">{t('Footer')}</h5>
                        
                        {/* Per-position mode selectors */}
                        <div className="grid grid-cols-3 gap-3 mb-4">
                            {(['Left', 'Center', 'Right'] as const).map((position) => {
                                const modeKey = `Footer${position}Mode` as const;
                                const textKey = `FooterText${position}` as const;
                                const imageKey = `Footer${position}Image` as const;
                                const mode = formik.values.Branding.HeaderFooter[modeKey] ?? 'none';
                                
                                return (
                                    <div key={position} className="space-y-2">
                                        <Dropdown
                                            value={mode}
                                            onChange={(value) => formik.setFieldValue(`Branding.HeaderFooter.${modeKey}`, value)}
                                            options={[
                                                { label: t('None'), value: 'none' },
                                                { label: t('Text'), value: 'text' },
                                                { label: t('Logo/Image'), value: 'logo' },
                                            ]}
                                            label={t(position)}
                                        />
                                        {mode === 'text' && (
                                            <Input
                                                name={textKey}
                                                value={formik.values.Branding.HeaderFooter[textKey] ?? ''}
                                                onChange={(e) => formik.setFieldValue(`Branding.HeaderFooter.${textKey}`, e.target.value)}
                                                placeholder={position === 'Left' ? t('e.g., Confidential') : position === 'Center' ? t('e.g., {page}') : t('e.g., Page {page}')}
                                                type="text"
                                            />
                                        )}
                                        {mode === 'logo' && (
                                            <SingleFileInput
                                                accept="image/*"
                                                file={isFile(formik.values.Branding.HeaderFooter[imageKey]) ? formik.values.Branding.HeaderFooter[imageKey] as File : null}
                                                setFile={(file) => formik.setFieldValue(`Branding.HeaderFooter.${imageKey}`, file)}
                                                existingFileUrl={
                                                    typeof formik.values.Branding.HeaderFooter[imageKey] === 'string' && formik.values.Branding.HeaderFooter[imageKey]
                                                        ? formik.values.Branding.HeaderFooter[imageKey] as string
                                                        : undefined
                                                }
                                                desc={t('Upload image')}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Footer font settings - show if any position has text */}
                        {(formik.values.Branding.HeaderFooter.FooterLeftMode === 'text' ||
                            formik.values.Branding.HeaderFooter.FooterCenterMode === 'text' ||
                            formik.values.Branding.HeaderFooter.FooterRightMode === 'text') && (
                            <div className="grid grid-cols-2 gap-4">
                                <Dropdown
                                    value={formik.values.Branding.HeaderFooter.FooterFontType ?? ''}
                                    onChange={(value) => formik.setFieldValue('Branding.HeaderFooter.FooterFontType', value)}
                                    options={[{ label: t('Use Default'), value: '' }, ...fontOptions]}
                                    label={t('Footer Font')}
                                />
                                <Input
                                    type="number"
                                    name="FooterFontSize"
                                    value={formik.values.Branding.HeaderFooter.FooterFontSize ?? 10}
                                    onChange={(e) =>
                                        formik.setFieldValue('Branding.HeaderFooter.FooterFontSize', clampNum(e.target.value, 8, 20))
                                    }
                                    label={t('Font Size (pt)')}
                                />
                            </div>
                        )}

                        {/* Footer logo size - show if any position has logo */}
                        {(formik.values.Branding.HeaderFooter.FooterLeftMode === 'logo' ||
                            formik.values.Branding.HeaderFooter.FooterCenterMode === 'logo' ||
                            formik.values.Branding.HeaderFooter.FooterRightMode === 'logo') && (
                            <div className="mt-3">
                                <Input
                                    type="number"
                                    name="FooterLogoSize"
                                    value={formik.values.Branding.HeaderFooter.FooterLogoSize ?? 18}
                                    onChange={(e) =>
                                        formik.setFieldValue('Branding.HeaderFooter.FooterLogoSize', clampNum(e.target.value, 12, 96))
                                    }
                                    label={t('Logo Size (pt)')}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default BrandingPanel;
